import { useMemo } from 'react';
import { AMP_SETTINGS } from '@ampsec/platform-client';
import { COMPLIANCE_TRAINING_MODULE_CONTENT } from 'Core-utils/constants/constants';
import { useTenantSettings } from 'Hooks/useTenantSettings';
import { useI18n } from 'Hooks/useI18n';
import {
    getComplianceTrainingCustomModulesSettingSignature,
    mergeComplianceTrainingModules,
    parseComplianceTrainingCustomModulesFromSettingSignature,
    parseComplianceTrainingModulesFromI18n,
} from 'Core-utils/i18n/complianceTrainingModuleI18n';

export interface ComplianceTrainingModuleOption {
    subject: string;
    name: string;
    description: string;
    videoUrl: string;
    thumbnailUrl?: string;
}

export const useComplianceTrainingModules = (): { modules: ComplianceTrainingModuleOption[]; loading: boolean } => {
    const { tenantSettings, settingsLoading } = useTenantSettings();
    const { t, loading: i18nLoading } = useI18n();

    const customModulesSettingSignature = useMemo(
        () =>
            getComplianceTrainingCustomModulesSettingSignature(
                tenantSettings as Record<string, { value?: unknown } | undefined>,
                AMP_SETTINGS?.tenant?.COMPLIANCE_TRAINING_CUSTOM_MODULES?.id,
            ),
        [tenantSettings],
    );

    const complianceTrainingSettingsSignature = useMemo(() => {
        const settingKey = AMP_SETTINGS?.tenant?.COMPLIANCE_TRAINING_SETTINGS?.id;
        if (!settingKey) {
            return '';
        }
        const raw = (tenantSettings as Record<string, { value?: unknown } | undefined>)?.[settingKey]?.value;
        if (raw === undefined || raw === null) {
            return '';
        }
        return typeof raw === 'string' ? raw : JSON.stringify(raw);
    }, [tenantSettings]);

    const activeSubjectsSignature = useMemo(() => {
        const settingKey = AMP_SETTINGS?.tenant?.COMPLIANCE_TRAINING_ACTIVE_SUBJECTS?.id;
        if (!settingKey) {
            return '';
        }
        const raw = (tenantSettings as Record<string, { value?: unknown } | undefined>)?.[settingKey]?.value;
        if (raw === undefined || raw === null) {
            return '';
        }
        return typeof raw === 'string' ? raw : JSON.stringify(raw);
    }, [tenantSettings]);

    const modules = useMemo(() => {
        let activeSubjects: string[] = [];
        try {
            activeSubjects = activeSubjectsSignature ? JSON.parse(activeSubjectsSignature) : [];
            if (!Array.isArray(activeSubjects)) {
                activeSubjects = [];
            }
        } catch {
            activeSubjects = [];
        }

        const settingsVideoBySubject: Record<string, { videoUrl: string; thumbnailUrl?: string }> = {};
        try {
            if (complianceTrainingSettingsSignature) {
                const parsed = JSON.parse(complianceTrainingSettingsSignature);
                const trainings = Array.isArray(parsed?.trainings) ? parsed.trainings : [];
                for (const record of trainings) {
                    const subject = record?.trainingConfig?.subject;
                    const videoUrl = record?.trainingConfig?.videoUrl;
                    if (subject && videoUrl) {
                        settingsVideoBySubject[subject] = {
                            videoUrl,
                            thumbnailUrl: record?.trainingConfig?.thumbnailUrl,
                        };
                    }
                }
            }
        } catch {
            /* ignore */
        }

        const contentfulModules = parseComplianceTrainingModulesFromI18n(t(COMPLIANCE_TRAINING_MODULE_CONTENT, '[]'));
        const customModules = parseComplianceTrainingCustomModulesFromSettingSignature(customModulesSettingSignature);

        return mergeComplianceTrainingModules(contentfulModules, customModules)
            .filter((subject) => activeSubjects.includes(subject.subject))
            .map((subject) => {
                const settingsOverride = settingsVideoBySubject[subject.subject];
                return {
                    subject: subject.subject,
                    name: subject.name,
                    description: subject.description,
                    videoUrl: settingsOverride?.videoUrl ?? subject.videoUrl,
                    thumbnailUrl: settingsOverride?.thumbnailUrl ?? subject.thumbnail,
                };
            })
            .filter((module) => Boolean(module.videoUrl));
    }, [t, customModulesSettingSignature, complianceTrainingSettingsSignature, activeSubjectsSignature]);

    return { modules, loading: settingsLoading || i18nLoading };
};
